import React, { useState, useEffect } from "react";
import { getData, postData, putData, deleteData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaPalette,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle,
  FaCode,
  FaFilter
} from "react-icons/fa";

function MaterialColorsManager() {
  const [materialColors, setMaterialColors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingMaterialColor, setEditingMaterialColor] = useState(null);
  const [deletingMaterialColor, setDeletingMaterialColor] = useState(null);
  const [filterMaterialId, setFilterMaterialId] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [permanentDeletingMaterialColor, setPermanentDeletingMaterialColor] = useState(null);
  const [formData, setFormData] = useState({
    material_id: '',
    color_id: '',
    color_code: '',
    active: 1
  });

  useEffect(() => {
    loadMaterialColors();
    loadMaterials();
    loadColors();
  }, []);

  const loadMaterialColors = async () => {
    try {
      setLoading(true);
      
      const response = await getData('/api/admin/get_material_colors');      
      if (response && response.data) {
        let materialColorsData = [];
        if (Array.isArray(response.data)) {
          materialColorsData = response.data;
        } else if (response.data.materialColors && Array.isArray(response.data.materialColors)) {
          materialColorsData = response.data.materialColors;
        } else {
          // Buscar cualquier array en la respuesta
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key]) && materialColorsData.length === 0) {
              materialColorsData = response.data[key];
            }
          });
        }
        
        setMaterialColors(materialColorsData);
      } else {
        setMaterialColors([]);
      }
      
    } catch (error) {
      console.error('Error cargando relaciones material-color:', error);
      toastError('Error al cargar las relaciones material-color');
      setMaterialColors([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMaterials = async () => {
    try {
      const response = await getData('/api/admin/get_materials');
      
      let materialsData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          materialsData = response.data;
        } else if (response.data.materials && Array.isArray(response.data.materials)) {
          materialsData = response.data.materials;
        }
      }
      
      // Filtrar solo materiales activos
      const activeMaterials = materialsData.filter(material => material.active);
      setMaterials(activeMaterials);
      
    } catch (error) {
      console.error('Error cargando materiales:', error);
      toastError('Error al cargar los materiales');
      setMaterials([]);
    }
  };

  const loadColors = async () => {
    try {
      const response = await getData('/api/admin/get_colors');
      
      let colorsData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          colorsData = response.data;
        } else if (response.data.colors && Array.isArray(response.data.colors)) {
          colorsData = response.data.colors;
        }
      }
      
      // Filtrar solo colores activos
      const activeColors = colorsData.filter(color => color.active);
      setColors(activeColors);
      
    } catch (error) {
      console.error('Error cargando colores:', error);
      toastError('Error al cargar los colores');
      setColors([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        material_id: parseInt(formData.material_id),
        color_id: parseInt(formData.color_id),
        color_code: formData.color_code.toUpperCase().trim(),
        active: formData.active
      };
      
      if (editingMaterialColor) {
        // Para actualizar necesitamos enviar los campos especiales según la API
        const updateData = {
          material_id: editingMaterialColor.material_id,
          color_code: editingMaterialColor.color_code, // Código actual para identificar
          new_color_id: parseInt(formData.color_id),
          new_color_code: formData.color_code.toUpperCase().trim(),
          active: formData.active
        };
        
        await putData('/api/admin/update_material_color', { data: updateData });
        toastSuccess(`Relación material-color actualizada exitosamente`);
      } else {
        await postData('/api/admin/create_material_color', { data: dataToSend });
        toastSuccess(`Relación material-color creada exitosamente`);
      }
      
      setShowModal(false);
      resetForm();
      await loadMaterialColors();
    } catch (error) {
      console.error('Error guardando relación material-color:', error);
      toastError(editingMaterialColor ? 'Error al actualizar la relación' : 'Error al crear la relación');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingMaterialColor(null);
    setFormData({
      material_id: '',
      color_id: '',
      color_code: '',
      active: 1
    });
  };

  const handleEdit = (materialColor) => {
    setEditingMaterialColor(materialColor);
    setFormData({
      material_id: materialColor.material_id || '',
      color_id: materialColor.color_id || '',
      color_code: materialColor.color_code || '',
      active: materialColor.active !== undefined ? materialColor.active : 1
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingMaterialColor(null);
    setFormData({
      material_id: '',
      color_id: '',
      color_code: '',
      active: 1
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;
    
    // Auto-formatear el código de color en mayúsculas
    if (name === 'color_code') {
      processedValue = value.toUpperCase();
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  const handleDelete = (materialColor) => {
    setDeletingMaterialColor(materialColor);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingMaterialColor) return;
    
    try {
      setLoading(true);
      
      // Para "eliminar" actualizamos a inactivo
      const updateData = {
        material_id: deletingMaterialColor.material_id,
        color_code: deletingMaterialColor.color_code,
        new_color_id: deletingMaterialColor.color_id,
        new_color_code: deletingMaterialColor.color_code,
        active: 0 // Marcar como inactivo
      };
      
      await putData('/api/admin/update_material_color', { data: updateData });
      toastSuccess(`Relación ${deletingMaterialColor.material_name} - ${deletingMaterialColor.color_name} eliminada exitosamente`);
      setShowDeleteModal(false);
      setDeletingMaterialColor(null);
      await loadMaterialColors();
    } catch (error) {
      console.error('Error eliminando relación:', error);
      toastError('Error al eliminar la relación');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingMaterialColor(null);
  };

  // Funciones de eliminación PERMANENTE
  const handlePermanentDelete = (materialColor) => {
    setPermanentDeletingMaterialColor(materialColor);
    setShowPermanentDeleteModal(true);
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeletingMaterialColor) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        material_id: permanentDeletingMaterialColor.material_id,
        color_id: permanentDeletingMaterialColor.color_id,
        user: 1
      };
      
      const response = await deleteData('/api/admin/delete_material_color_ref', dataToSend);
      
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      // Actualización optimista: eliminar de la lista
      setMaterialColors(prevMaterialColors => 
        prevMaterialColors.filter(mc => 
          !(mc.material_id === permanentDeletingMaterialColor.material_id && 
            mc.color_code === permanentDeletingMaterialColor.color_code)
        )
      );
      
      toastSuccess(`Relación ${permanentDeletingMaterialColor.material_name} - ${permanentDeletingMaterialColor.color_name} eliminada permanentemente`);
      setShowPermanentDeleteModal(false);
      setPermanentDeletingMaterialColor(null);
    } catch (error) {
      console.error('Error eliminando relación:', error);
      toastError(error.message || 'Error al eliminar la relación definitivamente');
    } finally {
      setLoading(false);
    }
  };

  const cancelPermanentDelete = () => {
    setShowPermanentDeleteModal(false);
    setPermanentDeletingMaterialColor(null);
  };

  const handleFilterChange = (e) => {
    setFilterMaterialId(e.target.value);
  };

  const clearFilter = () => {
    setFilterMaterialId('');
  };

  const getMaterialName = (materialId) => {
    const material = materials.find(m => m.id === materialId);
    return material ? material.material_name : `Material #${materialId}`;
  };

  const getColorName = (colorId) => {
    const color = colors.find(c => c.id === colorId);
    return color ? color.color_name : `Color #${colorId}`;
  };

  const generateSuggestedCode = () => {
    if (formData.material_id && formData.color_id) {
      const material = materials.find(m => m.id == formData.material_id);
      const color = colors.find(c => c.id == formData.color_id);
      
      if (material && color) {
        // Generar código sugerido: Primeras 2 letras del material + primeras 2 del color + 001
        const materialCode = material.material_name.substring(0, 2).toUpperCase();
        const colorCode = color.color_name.substring(0, 2).toUpperCase();
        const suggestedCode = `${materialCode}${colorCode}001`;
        
        setFormData(prev => ({
          ...prev,
          color_code: suggestedCode
        }));
      }
    }
  };

  // Filtrar datos mostrados
  const filteredMaterialColors = showInactive
    ? (filterMaterialId 
        ? materialColors.filter(mc => mc.material_id.toString() === filterMaterialId)
        : materialColors)
    : (filterMaterialId 
        ? materialColors.filter(mc => mc.material_id.toString() === filterMaterialId && mc.active)
        : materialColors.filter(mc => mc.active));

  return (
    <div className="material-colors-manager">
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-3">
            <FaPalette className="me-2 text-primary" />
            Relaciones Material-Color
          </h5>
          
          {/* Filtros */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <FaFilter />
                </span>
                <select
                  className="form-select"
                  value={filterMaterialId}
                  onChange={handleFilterChange}
                  disabled={loading}
                >
                  <option value="">
                    {materials.length === 0 ? 'No hay materiales disponibles' : 'Todos los materiales'}
                  </option>
                  {materials.map(material => (
                    <option key={material.id} value={material.id}>
                      {material.material_name}
                    </option>
                  ))}
                </select>
                {filterMaterialId && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={clearFilter}
                    disabled={loading}
                    title="Limpiar filtro"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-6 text-end">
              <div className="d-flex gap-2 justify-content-end align-items-center">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showInactiveToggle"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showInactiveToggle">
                    Mostrar inactivos
                  </label>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleNew}
                  disabled={loading}
                >
                  <FaPlus className="me-2" />
                  Nueva Relación
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card-body">
          {!showInactive && materialColors.length > filteredMaterialColors.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{materialColors.length - filteredMaterialColors.length}</strong> elemento(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando relaciones...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Color</th>
                    <th>Código</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMaterialColors.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        {filterMaterialId ? (
                          showInactive ? 'No hay relaciones para el material seleccionado' : 'No hay relaciones activas para el material seleccionado'
                        ) : (
                          showInactive ? 'No hay relaciones registradas' : 'No hay relaciones activas. Activa el toggle "Mostrar inactivos" para ver relaciones eliminadas.'
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredMaterialColors.map((mc, index) => (
                      <tr key={`${mc.material_id}-${mc.color_id}-${index}`}>
                        <td>
                          <span className="badge bg-info">
                            {mc.material_name}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-warning text-dark">
                            {mc.color_name}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-dark">
                            <FaCode className="me-1" />
                            {mc.color_code}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${mc.active ? 'bg-success' : 'bg-danger'}`}>
                            {mc.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {mc.inserted_date ? new Date(mc.inserted_date).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td className="text-center">
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(mc)}
                              disabled={loading}
                              title="Editar relación"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => handleDelete(mc)}
                              disabled={loading}
                              title={mc.active ? "Inactivar relación" : "Activar relación"}
                            >
                              <FaTrash />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handlePermanentDelete(mc)}
                              disabled={loading}
                              title="Eliminar definitivamente (irreversible)"
                            >
                              <FaExclamationTriangle />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar relación */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingMaterialColor ? 'Editar Relación Material-Color' : 'Nueva Relación Material-Color'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Material *</label>
                    <select
                      className="form-select"
                      name="material_id"
                      value={formData.material_id}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="">
                        {materials.length === 0 ? 'No hay materiales disponibles' : 'Seleccionar Material'}
                      </option>
                      {materials.map(material => (
                        <option key={material.id} value={material.id}>
                          {material.material_name}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      {materials.length === 0 
                        ? 'No se encontraron materiales activos.'
                        : 'Selecciona el material base'
                      }
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Color *</label>
                    <select
                      className="form-select"
                      name="color_id"
                      value={formData.color_id}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="">
                        {colors.length === 0 ? 'No hay colores disponibles' : 'Seleccionar Color'}
                      </option>
                      {colors.map(color => (
                        <option key={color.id} value={color.id}>
                          {color.color_name}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      {colors.length === 0 
                        ? 'No se encontraron colores activos.'
                        : 'Selecciona el color asociado'
                      }
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Código de Color *</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        name="color_code"
                        value={formData.color_code}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        placeholder="Ej: PN001, TB002, etc."
                        maxLength="10"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={generateSuggestedCode}
                        disabled={loading || !formData.material_id || !formData.color_id}
                        title="Generar código sugerido"
                      >
                        <FaCode />
                      </button>
                    </div>
                    <div className="form-text">
                      Código único para esta combinación material-color (ej: PANA NEGRO = PN001)
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="active"
                        checked={formData.active === 1}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          active: e.target.checked ? 1 : 0
                        }))}
                        disabled={loading}
                      />
                      <label className="form-check-label">
                        Relación activa
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.material_id || !formData.color_id || !formData.color_code.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingMaterialColor ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar la relación:</p>
                <div className="alert alert-info">
                  <strong>Material:</strong> {deletingMaterialColor?.material_name}<br/>
                  <strong>Color:</strong> {deletingMaterialColor?.color_name}<br/>
                  <strong>Código:</strong> {deletingMaterialColor?.color_code}
                </div>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  La relación será marcada como inactiva y no aparecerá en las opciones disponibles.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación PERMANENTE */}
      {showPermanentDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="me-2" />
                  ⚠️ ELIMINAR DEFINITIVAMENTE
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE</strong>
                </div>
                <p>¿Está COMPLETAMENTE seguro que desea eliminar permanentemente la siguiente relación material-color?</p>
                {permanentDeletingMaterialColor && (
                  <div className="alert alert-warning">
                    <strong>Material:</strong> {permanentDeletingMaterialColor.material_name}<br/>
                    <strong>Color:</strong> {permanentDeletingMaterialColor.color_name}<br/>
                    <strong>Código:</strong> {permanentDeletingMaterialColor.color_code}
                  </div>
                )}
                <ul className="text-danger">
                  <li>La relación se eliminará <strong>permanentemente</strong> de la base de datos</li>
                  <li>NO se podrá recuperar esta información</li>
                  <li>Puede afectar pedidos y referencias existentes</li>
                </ul>
                <p className="mt-3">
                  <small className="text-muted">
                    💡 <strong>Recomendación:</strong> Si solo desea ocultar temporalmente esta relación,
                    use el botón de "Inactivar" en su lugar.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmPermanentDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle className="me-2" />
                      ⚠️ SÍ, ELIMINAR DEFINITIVAMENTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaterialColorsManager;
